// / <reference path="./FootballMatchup.d.ts" />

import React, { Fragment } from 'react';
import MatchupComponent from '~/components/Matchup/Matchup';
import ScoringSummary from '~/components/ScoringSummary/ScoringSummary';
import LineScoreCard from '~/components/LineScoreCard/LineScoreCard';
import GameDetails from '~/components/GameDetailsCard/GameDetailsCard';
import formatKeyPlayers from '~/util/formatKeyPlayers';
import KeyPlayersCard from '~/components/KeyPlayersCard/KeyPlayersCard';
import TeamStats from '~/components/TeamStats/TeamStats';
import TeamStatsGetTheApp from '~/components/TeamStatsGetTheApp/TeamStatsGetTheApp';
import StyledComponent from '~/components/Styled/Styled';
import normalizeFootballEvent from '~/util/normalizeFootballEvent';
import MatchupSection from '~/components/MatchupSection/MatchupSection';
import Ad, { AdTypes } from '~/containers/AdContainer/AdContainer';

import { NFL, NFL_PREGAME_DEFENSIVE, NFL_PREGAME_OFFENSIVE } from '~/schemas/teamStats/nflSchema';
import normalise from '~/schemas/teamStats/normalise';

import getOr from '~/util/getOr';

import { injectIntl } from 'react-intl';
import messages from '~/constants/localizations/matchup';

import css from './FootballMatchup.scss';

type Props = {
  event: Event;
  stats: any;
  intl: any;
};

class FootballMatchup extends React.Component<Props> {
  static displayName = 'FootballMatchup';

  renderGameDetails(event) {
    return (
      <MatchupSection>
        <GameDetails disableHeaderDetails eventDetails={event.event_details} />
      </MatchupSection>
    );
  }

  renderKeyPlayers(event) {
    if (event.event_status === 'in_progress') return null;
    const { formatMessage } = this.props.intl;

    const keyPlayers = formatKeyPlayers(event);
    const title =
      event.event_status === 'final'
        ? formatMessage(messages.top_performers)
        : formatMessage(messages.keyPlayers);

    return (
      <MatchupSection>
        <KeyPlayersCard title={title} playersByHeaders={keyPlayers} />
      </MatchupSection>
    );
  }

  renderLineScoreCard(event) {
    if (event.event_status === 'pre_game') return null;
    return (
      <MatchupSection>
        <LineScoreCard event={event} />
      </MatchupSection>
    );
  }

  renderMatchup(event) {
    return (
      <MatchupSection>
        <MatchupComponent event={event} />
      </MatchupSection>
    );
  }

  renderScoringSummary(event) {
    const { formatMessage } = this.props.intl;
    const scoringSummary = getOr(event, 'box_score.scoring_summary', []);

    return (
      <MatchupSection>
        <h2 className={css.header}>{formatMessage(messages.scoringSummary)}</h2>
        <ScoringSummary scorings={scoringSummary} sportType="football" />
      </MatchupSection>
    );
  }

  renderTeamStats(event, stats) {
    const preGame = event.event_status === 'pre_game';
    const homeTeamName = getOr(event, 'home_team.abbreviation', '');
    const awayTeamName = getOr(event, 'away_team.abbreviation', '');
    const { formatMessage } = this.props.intl;

    if (preGame) {
      return (
        <Fragment>
          <div className={css.desktopView}>
            <MatchupSection>
              <div className={css.teamStatsCard}>
                <div className={css.header}>{formatMessage(messages.offensiveStats)}</div>
                <TeamStats
                  homeTeamName={homeTeamName}
                  awayTeamName={awayTeamName}
                  stats={normalise(stats.offensive, NFL_PREGAME_OFFENSIVE)}
                />
              </div>
            </MatchupSection>
            <MatchupSection>
              <div className={css.teamStatsCard}>
                <div className={css.header}>{formatMessage(messages.defensiveStats)}</div>
                <TeamStats
                  homeTeamName={homeTeamName}
                  awayTeamName={awayTeamName}
                  stats={normalise(stats.defensive, NFL_PREGAME_DEFENSIVE)}
                />
              </div>
            </MatchupSection>
          </div>

          <div className={css.mobileView}>
            <MatchupSection>
              <div className={css.teamStatsCard}>
                <div className={css.header}>{formatMessage(messages.offensiveStats)}</div>
                <TeamStats
                  homeTeamName={homeTeamName}
                  awayTeamName={awayTeamName}
                  stats={normalise(stats.offensive, NFL_PREGAME_OFFENSIVE)}
                />
                {this.props.intl.locale === 'en' ? (
                  <div className={css.teamStatsGetTheApp}>
                    <TeamStatsGetTheApp leagueSlug="nfl" />
                  </div>
                ) : null}
              </div>
            </MatchupSection>
          </div>
        </Fragment>
      );
    }

    return (
      <MatchupSection>
        <div className={css.teamStatsCard}>
          <h2 className={css.header}>{formatMessage(messages.teamStats)}</h2>
          <TeamStats
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            stats={normalise(stats.team, NFL)}
          />
          {this.props.intl.locale === 'en' ? (
            <div className={`${css.teamStatsGetTheApp} ${css.mobileView}`}>
              <TeamStatsGetTheApp leagueSlug="nfl" />
            </div>
          ) : null}
        </div>
      </MatchupSection>
    );
  }
  render() {
    const event = normalizeFootballEvent(this.props.event);
    const stats = this.props.stats;
    const preGame = event.event_status === 'pre_game';
    const leagueSlug = getOr(this.props.event, 'league.slug', '');

    return (
      <div className="row">
        <div className={css.desktopView}>
          <div className={`${css.column} col-sm-8`}>
            {this.renderMatchup(event)}
            {!preGame && this.renderScoringSummary(event)}
            {this.renderTeamStats(event, stats)}
          </div>
          <div className={`${css.column} col-sm-4`}>
            {this.renderGameDetails(event)}
            <Ad leagueSlug={leagueSlug} adType={AdTypes.BIGBOX_DESKTOP} />
            {this.renderLineScoreCard(event)}
            {this.renderKeyPlayers(event)}
          </div>
        </div>
        <div className={css.mobileView}>
          {this.renderMatchup(event)}
          {this.renderLineScoreCard(event)}
          {!preGame && this.renderScoringSummary(event)}
          {this.renderTeamStats(event, stats)}
          {this.renderKeyPlayers(event)}
          {this.renderGameDetails(event)}
        </div>
      </div>
    );
  }
}
export default injectIntl(StyledComponent(FootballMatchup, [css]));
